/**
 * Gallery preview modal per ED-A12 §C2 (MVP slice): compiled-HTML
 * preview with a Desktop / Mobile width toggle and a "Use template"
 * shortcut. Dark-mode preview joins when the render pipeline grows a
 * dark variant (§C2 lists it; render-service doesn't emit one yet).
 *
 * The HTML goes into a fully sandboxed iframe via srcdoc — no scripts,
 * no same-origin: catalog content is trusted-ish (we author it), but
 * Phase-8 community templates won't be, so the boundary starts strict.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { previewGalleryItem, type GalleryItemSummary } from "../lib/gallery";
import { Button } from "./ui/button";

const WIDTHS = { desktop: 640, mobile: 375 } as const;
type ViewMode = keyof typeof WIDTHS;

export function GalleryPreviewModal({
  item,
  onUse,
  onClose,
}: {
  item: GalleryItemSummary;
  onUse: () => void;
  onClose: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<ViewMode>("desktop");

  const preview = useQuery({
    queryKey: ["template-gallery", "preview", item.id],
    queryFn: () => previewGalleryItem(item.id),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${item.name}`}
        className="flex h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-neutral-900">{item.name}</h2>
            <p className="truncate text-xs text-neutral-500">
              {item.category.industry} · {item.category.use_case} · Free to use
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-neutral-300" role="group" aria-label="Preview width">
              {(Object.keys(WIDTHS) as ViewMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  className={
                    "px-3 py-1 text-xs font-medium capitalize first:rounded-l-md last:rounded-r-md " +
                    (mode === m ? "bg-neutral-900 text-white" : "bg-white text-neutral-700")
                  }
                >
                  {m}
                </button>
              ))}
            </div>
            <Button type="button" variant="primary" size="sm" onClick={onUse}>
              Use template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onClose} aria-label="Close preview">
              ✕
            </Button>
          </div>
        </header>

        <div className="flex flex-1 items-start justify-center overflow-auto bg-neutral-100 p-4">
          {preview.isLoading && (
            <p className="mt-12 text-sm text-neutral-500">Rendering preview…</p>
          )}
          {preview.error !== null && !preview.isLoading && (
            <p role="alert" className="mt-12 text-sm text-neutral-500">
              Couldn&apos;t render the preview — you can still use the template.
            </p>
          )}
          {preview.data !== undefined && (
            <iframe
              title={`${item.name} preview`}
              sandbox=""
              srcDoc={preview.data.html}
              style={{ width: WIDTHS[mode] }}
              className="h-full shrink-0 rounded border border-neutral-200 bg-white shadow-sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}
